import { BitmapLayer } from '@deck.gl/layers/typed';
import { Bbox } from 'ais-facade-api/api';
import { useLazyGetPressureQuery } from 'app/api/weather';
import { useAppSelector } from 'app/store/store';
import { normalizeWeatherData, toEPSG3857 } from 'app/helpers/weather/dataProcessor';
import { useEffect, useState } from 'react';

const PRESSURE_PALETTE = [
  [68, 1, 84, 255], // #440154
  [59, 82, 139, 255], // #3b528b
  [33, 144, 141, 255], // #21908d
  [93, 201, 99, 255], // #5dc963
  [253, 231, 37, 255], // #fde725
];
const getColor = (value: number) => {
  // 2 соседние точки в палитре
  const index = Math.floor(value * (PRESSURE_PALETTE.length - 1));
  const nextIndex = Math.min(index + 1, PRESSURE_PALETTE.length - 1);
  const color1 = PRESSURE_PALETTE[index];
  const color2 = PRESSURE_PALETTE[nextIndex];

  const ratio = value * (PRESSURE_PALETTE.length - 1) - index;

  // Интерполируем цвет
  const interpolatedColor = color1.map((c, i) => Math.round(c + (color2[i] - c) * ratio));

  return interpolatedColor;
};

export const usePressureLayer = (bbox: Bbox, viewState: Record<string, number>, isWeatherLayerHiddenMode: boolean) => {
  const [coloredImage, setColoredImage] = useState<ArrayBuffer | string | null>(null);
  const { width, height } = viewState;

  const [getPressure, { ...getPressureRequest }] = useLazyGetPressureQuery();

  const visible = useAppSelector((state) => state.weatherSlice.layersVisibility.pressure) && !isWeatherLayerHiddenMode;
  const user = useAppSelector((state) => state.userSlice.user);

  const canvas = document.createElement('canvas') as HTMLCanvasElement;
  const ctx = canvas?.getContext('2d');
  const createColoredMap = async (normalizedData: number[]) => {
    const coloredCanvas = document.createElement('canvas');
    const coloredCtx = coloredCanvas.getContext('2d');

    coloredCanvas.width = viewState.width;
    coloredCanvas.height = viewState.height;

    if (coloredCtx) {
      const coloredData = coloredCtx.createImageData(coloredCanvas.width, coloredCanvas.height);

      for (let i = 0; i < normalizedData.length; i++) {
        const [r, g, b, a] = getColor(normalizedData[i]);
        coloredData.data[i * 4] = r;
        coloredData.data[i * 4 + 1] = g;
        coloredData.data[i * 4 + 2] = b;
        coloredData.data[i * 4 + 3] = a;
      }

      coloredCtx.putImageData(coloredData, 0, 0);

      return new Promise((resolve) => {
        coloredCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    }
  };

  useEffect(() => {
    canvas.width = viewState.width;
    canvas.height = viewState.height;
  }, []);

  const worldBbox: Bbox = {
    first: { latitude: -85, longitude: -180 },
    second: { latitude: 85, longitude: 180 },
    z: 3,
  };

  useEffect(() => {
    if (user && typeof bbox?.first?.latitude === 'number' && ctx && visible) {
      if (width && height && !coloredImage) {
        canvas.width = width;
        canvas.height = height;
        const convertedBbox = toEPSG3857(worldBbox);
        getPressure({
          bbox: convertedBbox,
          width,
          height,
        })
          .unwrap()
          .then((res: Blob) => {
            const imageUrl = URL.createObjectURL(res);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
              ctx.clearRect(0, 0, Number(width), Number(height));
              ctx.drawImage(image, 0, 0);
              const imageData = ctx.getImageData(0, 0, Number(width), Number(height));
              const data = imageData.data;

              const normalizedData = normalizeWeatherData(data);
              createColoredMap(normalizedData).then((blob) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob as Blob);
                reader.onloadend = () => {
                  setColoredImage(reader.result);
                };
              });
            };
          });
      }
    } else {
      setColoredImage(null);
    }
  }, [user, visible, viewState]);

  const layer = coloredImage
    ? new BitmapLayer({
        id: 'layer-pressure',
        image: coloredImage,
        bounds: [-180, -85, 180, 85],
        visible: !getPressureRequest.isFetching && visible,
        pickable: false,
        opacity: .35,
      })
    : null;
  return layer;
};
